import { useEffect, useState } from 'react';
import { useWebSocket } from './use-websocket';
import { useToast } from '@/hooks/use-toast';
import { queryClient } from '@/lib/queryClient';

export function useRealtimeUpdates() {
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const { toast } = useToast();

  const { isConnected } = useWebSocket({
    onMessage: (message) => {
      handleWebSocketMessage(message);
    },
    onConnect: () => {
      console.log('Real-time updates connected');
    },
    onDisconnect: () => {
      console.log('Real-time updates disconnected');
    }
  });

  const handleWebSocketMessage = (message: any) => {
    setLastUpdate(new Date());
    
    switch (message.type) {
      case 'product_updated':
        // Invalidate product queries to refetch updated data
        queryClient.invalidateQueries({ queryKey: ['/api/products'] });
        // Note: Removed price update notifications per user request
        break;
        
      case 'product_created':
        queryClient.invalidateQueries({ queryKey: ['/api/products'] });
        break;
        
      case 'product_deleted':
        queryClient.invalidateQueries({ queryKey: ['/api/products'] });
        break;
        
      case 'products_imported':
        queryClient.invalidateQueries({ queryKey: ['/api/products'] });
        toast({
          title: "Products Imported",
          description: `${message.data.length} products have been imported`,
          duration: 3000,
        });
        break;
        
      case 'orderDeleted':
        // Invalidate restaurant order queries when an order is deleted
        queryClient.invalidateQueries({ queryKey: ['admin-restaurant-orders'] });
        queryClient.invalidateQueries({ queryKey: ['admin-restaurant-stats'] });
        queryClient.invalidateQueries({ queryKey: ['admin-rebates'] });
        console.log(`Order ${message.data.orderNumber} deleted via WebSocket`);
        break;
        
      case 'restaurant_order_status_updated':
        // Invalidate restaurant order queries when status changes
        queryClient.invalidateQueries({ queryKey: ['admin-restaurant-orders'] });
        queryClient.invalidateQueries({ queryKey: ['admin-restaurant-stats'] });
        break;
        
      default:
        console.log('Unknown WebSocket message type:', message.type);
    }
  };

  // Auto-refresh every 30 seconds as fallback
  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getTimeSinceLastUpdate = () => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} sec ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min ago`;
    } else {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
  };

  return {
    isConnected,
    lastUpdate,
    getTimeSinceLastUpdate
  };
}
